// GridSection.js
import React, { useEffect, useState } from "react";
import { Switch, NumericInput, Alignment } from "@blueprintjs/core";
import { reaction } from "mobx";
import * as svg from "polotno/utils/svg";
import { observer } from "mobx-react-lite";
import { SectionTab } from "polotno/side-panel";
import BsFillGrid3X3GapFill from "@meronex/icons/bs/BsFillGrid3X3GapFill";
import { trackAnalyticsEvent } from "../../../utils/analytics";

export const GridSection = {
  name: "custom-grid",

  Tab: (props) => (
    <SectionTab
      name="Grid"
      {...props}
      onClick={(event) => {
        trackAnalyticsEvent("design_sidebar_tab_click", {
          section: "custom_grid",
        });
        props.onClick?.(event);
      }}
    >
      <BsFillGrid3X3GapFill />
    </SectionTab>
  ),

  Panel: observer(({ store }) => {
    if (!store.activePage) return null;

    {
      if (!store.activePage) return null;

      const [rowsNumber, setRowsNumber] = useState(5);
      const [colsNumber, setColsNumber] = useState(5);
      const [visible, setVisible] = useState(false);

      const clearGrid = () => {
        const gridElements = store.activePage.children.filter(
          (child) => child.name === "grid"
        );
        store.deleteElements(gridElements.map((el) => el.id));
      };

      const createDot = (x, y) => {
        const template = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"></svg>`;
        const url = svg.svgToURL(template);
        store.activePage.addElement({
          x,
          y,
          type: "svg",
          width: 1,
          height: 1,
          src: url,
          name: "grid",
          selectable: false,
        });
      };

      const generateGrid = (rows, cols) => {
        clearGrid();
        const { width, height } = store.activePage;
        const dx = width / cols;
        const dy = height / rows;

        const template = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      ${[...Array(cols - 1)]
        .map(
          (_, i) =>
            `<line x1="${dx * (i + 1)}" y1="0" x2="${
              dx * (i + 1)
            }" y2="${height}" stroke="black" stroke-width="1" stroke-opacity="0.8"/>`
        )
        .join("")}
      ${[...Array(rows - 1)]
        .map(
          (_, i) =>
            `<line x1="0" y1="${dy * (i + 1)}" x2="${width}" y2="${
              dy * (i + 1)
            }" stroke="black" stroke-width="1" stroke-opacity="0.5"/>`
        )
        .join("")}
    </svg>`;

        const url = svg.svgToURL(template);

        store.activePage.addElement({
          x: 0,
          y: 0,
          width,
          height,
          type: "svg",
          src: url,
          name: "grid",
          selectable: false,
          opacity: 1,
          alwaysOnTop: true,
        });

        [...Array(cols - 1)].forEach((_, i) => createDot(dx * (i + 1), 0));
        [...Array(rows - 1)].forEach((_, i) => createDot(0, dy * (i + 1)));
      };

      useEffect(() => {
        const dispose = reaction(
          () => [store.width, store.height],
          () => {
            if (visible) {
              generateGrid(rowsNumber, colsNumber);
            }
          }
        );
        return () => dispose();
      }, [store, visible, rowsNumber, colsNumber]);

      useEffect(() => {
        visible ? generateGrid(rowsNumber, colsNumber) : clearGrid();
      }, [visible, rowsNumber, colsNumber]);

      return (
        <div>
          <Switch
            checked={visible}
            onChange={(e) => {
              const enabled = e.target.checked;
              setVisible(enabled);
              trackAnalyticsEvent("design_grid_toggle", {
                enabled,
                rows: rowsNumber,
                cols: colsNumber,
              });
            }}
            alignIndicator={Alignment.RIGHT}
            style={{ marginTop: "8px", marginBottom: "25px" }}
          >
            Show grid
          </Switch>

          <div style={{ paddingBottom: "10px" }}>
            <div style={{ width: "50%", display: "inline-block" }}>Rows:</div>
            <div style={{ width: "50%", display: "inline-block" }}>
              <NumericInput
                fill
                value={rowsNumber}
                onValueChange={(val) => {
                  const nextRows = val || 1;
                  setRowsNumber(nextRows);
                  trackAnalyticsEvent("design_grid_rows_change", {
                    rows: nextRows,
                  });
                }}
                min={1}
                max={100}
              />
            </div>
          </div>

          <div>
            <div style={{ width: "50%", display: "inline-block" }}>Cols:</div>
            <div style={{ width: "50%", display: "inline-block" }}>
              <NumericInput
                fill
                value={colsNumber}
                onValueChange={(val) => {
                  const nextCols = val || 1;
                  setColsNumber(nextCols);
                  trackAnalyticsEvent("design_grid_cols_change", {
                    cols: nextCols,
                  });
                }}
                min={1}
                max={100}
              />
            </div>
          </div>
        </div>
      );
    }
  }),
};
